/**
 * TemplateCardGrid - Responsive grid layout for template cards
 *
 * Features:
 * - Responsive grid (1 col mobile, 2 col tablet, 3 col desktop)
 * - Staggered animation on load
 * - Empty state handling
 * - Skeleton loading state
 */
import { motion } from 'framer-motion';
import { FileText, Plus } from 'lucide-react';
import { Button, cn } from '@respectly/ui';
import type { GlobalTemplate } from '@respectly/shared-types';
import { TemplateCard, TemplateCardSkeleton } from './TemplateCard';

// ============================================
// Types
// ============================================

interface TemplateCardGridProps {
  templates: GlobalTemplate[];
  isLoading?: boolean;
  onEdit?: (template: GlobalTemplate) => void;
  onDuplicate?: (template: GlobalTemplate) => void;
  onDelete?: (template: GlobalTemplate) => void;
  onPreview?: (template: GlobalTemplate) => void;
  onVisibilityChange?: (template: GlobalTemplate) => void;
  onCreateNew?: () => void;
  className?: string;
}

// ============================================
// Animation Variants
// ============================================

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3 },
  },
};

// ============================================
// Component
// ============================================

export function TemplateCardGrid({
  templates,
  isLoading,
  onEdit,
  onDuplicate,
  onDelete,
  onPreview,
  onVisibilityChange,
  onCreateNew,
  className,
}: TemplateCardGridProps) {
  // Loading state
  if (isLoading) {
    return (
      <div className={cn('grid gap-4 sm:grid-cols-2 lg:grid-cols-3', className)}>
        {Array.from({ length: 6 }).map((_, i) => (
          <TemplateCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  // Empty state
  if (templates.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-16 h-16 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
          <FileText className="h-8 w-8 text-slate-400 dark:text-slate-500" />
        </div>
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">
          No templates found
        </h3>
        <p className="text-sm text-slate-500 dark:text-slate-400 max-w-sm mb-6">
          Create your first template to share standardized content with your tenants.
        </p>
        {onCreateNew && (
          <Button onClick={onCreateNew}>
            <Plus className="mr-2 h-4 w-4" />
            Create Template
          </Button>
        )}
      </div>
    );
  }

  // Grid with templates
  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className={cn('grid gap-4 sm:grid-cols-2 lg:grid-cols-3', className)}
    >
      {templates.map((template) => (
        <motion.div key={template.id} variants={itemVariants}>
          <TemplateCard
            template={template}
            onEdit={onEdit}
            onDuplicate={onDuplicate}
            onDelete={onDelete}
            onPreview={onPreview}
            onVisibilityChange={onVisibilityChange}
          />
        </motion.div>
      ))}
    </motion.div>
  );
}
